import axios from 'axios';
import { clearAdminSessionMeta } from './adminPermissions';
import { isJwtExpiredByDailyMidnight } from './jwtDailyExpiry';
import { UPLOAD_CONFIG } from './uploadConfig';
import { runWithRetry } from './uploadImageUtils';

const ADMIN_TOKEN_KEY = 'ipe_admin_token';

const adminApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

adminApi.interceptors.request.use((config) => {
  const token = peekAdminToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && body.code === 200) {
      return body.data;
    }
    return Promise.reject(new Error(body?.message || '请求失败'));
  },
  (error) => {
    if (error.response?.status === 401) {
      redirectAdminLogin();
    }
    const body = error.response?.data;
    const msg = body?.message || body?.error || error.message || '请求失败';
    const err = new Error(msg);
    if (Array.isArray(body?.failures)) {
      err.failures = body.failures;
    }
    if (body?.failureCount != null) {
      err.failureCount = Number(body.failureCount) || body.failures?.length || 0;
    }
    return Promise.reject(err);
  }
);

export function peekAdminToken() {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  return token && String(token).trim() ? String(token).trim() : null;
}

export function buildAdminAuthHeaders(extraHeaders = {}) {
  const token = peekAdminToken();
  if (!token) return { ...extraHeaders };
  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

export function getAdminToken() {
  const trimmed = peekAdminToken();
  if (!trimmed) return null;
  if (isJwtExpiredByDailyMidnight(trimmed)) {
    setAdminToken(null);
    return null;
  }
  return trimmed;
}

export function setAdminToken(token) {
  if (token) {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    clearAdminSessionMeta();
  }
}

/**
 * 仅在运营后台路径内做硬跳转。
 * 工作台误调 adminAPI（如自动规则类目）拿到 401 时不得把用户整页踢到 /ipe/admin/login。
 */
export function redirectAdminLogin() {
  setAdminToken(null);
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  const onAdminConsole = path === '/ipe/admin' || path.startsWith('/ipe/admin/');
  if (!onAdminConsole) return;
  if (path.includes('/ipe/admin/login')) return;
  window.location.href = '/ipe/admin/login';
}

export { getAdminRole, getAdminPermissions, adminRoleLabel } from './adminPermissions';

export const adminAPI = {
  login: (phone, password, captchaToken) => adminApi.post('/admin/auth/login', { phone, password, captchaToken }),
  getMe: () => adminApi.get('/admin/auth/me'),
  getDashboard: (params) => adminApi.get('/admin/dashboard', { params }),
  listJobTasks: (params) => adminApi.get('/admin/job-tasks', { params }),
  getTaskQueues: () => adminApi.get('/admin/task-queues'),
  listTaskQueueItems: (module, params) => adminApi.get(`/admin/task-queues/${module}/items`, { params }),
  getTaskQueueItem: (module, id) => adminApi.get(`/admin/task-queues/${module}/items/${id}`),
  retryTaskQueueItem: (module, id) => adminApi.post(`/admin/task-queues/${module}/items/${id}/retry`, {}),
  cancelTaskQueueItem: (module, id) => adminApi.post(`/admin/task-queues/${module}/items/${id}/cancel`, {}),
  getJobTasksByWorkspace: () => adminApi.get('/admin/job-tasks/by-workspace'),
  getStorageCapacity: () => adminApi.get('/admin/storage-capacity'),
  refreshStorageCapacity: () => adminApi.post('/admin/storage-capacity/refresh', {}, { timeout: 120000 }),
  getStorageCapacityDatabaseTables: () => adminApi.get('/admin/storage-capacity/database-tables'),
  getStorageCapacityUserAllocations: () => adminApi.get('/admin/storage-capacity/user-allocations'),
  getStorageAclStatus: () => adminApi.get('/admin/storage-acl/status'),
  backfillStorageAcl: (body) => adminApi.post('/admin/storage-acl/backfill', body || {}, { timeout: 300000 }),
  diagnoseStorageAcl: ({ url, userId } = {}) => adminApi.get('/admin/storage-acl/diagnose', {
    params: {
      url,
      ...(userId == null || userId === '' ? {} : { userId }),
    },
  }),
  getBigScreen: (params) => adminApi.get('/admin/big-screen', { params }),
  getLoginEvents: (params) => adminApi.get('/admin/login-events', { params }),
  listLoginLocks: (params) => adminApi.get('/admin/login-locks', { params }),
  unlockLoginLockByIdentity: (data = {}) => adminApi.post('/admin/login-locks/unlock', data),
  getCreditConsumptionLogs: (params) => adminApi.get('/admin/credit-consumption-logs', { params }),
  getClientErrorLogs: (params) => adminApi.get('/admin/client-error-logs', { params }),
  getClientErrorLogDetail: (id) => adminApi.get(`/admin/client-error-logs/${id}`),
  listDataGovernanceCollections: () => adminApi.get('/admin/data-governance/collections'),
  listDataGovernanceRecycleBin: (collectionKey, params) =>
    adminApi.get(`/admin/data-governance/recycle-bin/${encodeURIComponent(collectionKey)}`, { params }),
  getDataGovernanceRecycleBinItem: (collectionKey, recordId) =>
    adminApi.get(`/admin/data-governance/recycle-bin/${encodeURIComponent(collectionKey)}/${encodeURIComponent(recordId)}`),
  restoreDataGovernanceItem: (collectionKey, recordId) =>
    adminApi.post(`/admin/data-governance/recycle-bin/${encodeURIComponent(collectionKey)}/${encodeURIComponent(recordId)}/restore`),
  purgeDataGovernanceItem: (collectionKey, recordId) =>
    adminApi.post(`/admin/data-governance/recycle-bin/${encodeURIComponent(collectionKey)}/${encodeURIComponent(recordId)}/purge`),
  listDataGovernanceArchiveLogs: (params) => adminApi.get('/admin/data-governance/archive-logs', { params }),
  listSheinWebhooks: (params) => adminApi.get('/admin/platform/shein/webhooks', { params }),
  getSheinWebhook: (id) => adminApi.get(`/admin/platform/shein/webhooks/${id}`),
  replaySheinWebhook: (id) => adminApi.post(`/admin/platform/shein/webhooks/${id}/replay`),
  getUsers: (params) => adminApi.get('/admin/users', { params }),
  getPortalUsers: (params) => adminApi.get('/admin/portal-users', { params }),
  getAuthorizedShops: (params) => adminApi.get('/admin/shops', { params }),
  unbindAuthorizedShop: (id) => adminApi.post(`/admin/shops/${id}/unbind`),
  getWorkspaceScopes: () => adminApi.get('/admin/users/workspace-scopes'),
  getPortalWorkspaceScopes: () => adminApi.get('/admin/portal-users/workspace-scopes'),
  searchUsers: (keyword) => adminApi.get('/admin/users/search', { params: { keyword } }),
  createUser: (data) => adminApi.post('/admin/users', data),
  createPortalUser: (data) => adminApi.post('/admin/portal-users', data),
  updateUser: (id, data) => adminApi.put(`/admin/users/${id}`, data),
  updatePortalUser: (id, data) => adminApi.put(`/admin/portal-users/${id}`, data),
  setUserStatus: (id, disabled) => adminApi.patch(`/admin/users/${id}/status`, { disabled }),
  setUserSuspended: (id, suspended) => adminApi.patch(`/admin/users/${id}/suspended`, { suspended }),
  setPortalUserStatus: (id, disabled) => adminApi.patch(`/admin/portal-users/${id}/status`, { disabled }),
  deleteUser: (id) => adminApi.delete(`/admin/users/${id}`),
  deletePortalUser: (id) => adminApi.delete(`/admin/portal-users/${id}`),
  resetUserRecharge: (id) => adminApi.post(`/admin/users/${id}/reset-recharge`),
  unlockUserLoginLock: (id) => adminApi.post(`/admin/users/${id}/unlock-login-lock`),
  unlockUserSmsSendLock: (id) => adminApi.post(`/admin/users/${id}/unlock-sms-send-lock`),
  changeUserPassword: (id, data) => adminApi.post(`/admin/users/${id}/password`, data),
  getPlanTiers: () => adminApi.get('/admin/levels/plan-tiers'),
  recharge: (data) => adminApi.post('/admin/finance/recharge', data),
  getFinanceOperations: (params) => adminApi.get('/admin/finance/operations', { params }),
  getCreditPacks: () => adminApi.get('/admin/finance/credit-packs'),
  saveCreditPacks: (packs) => adminApi.put('/admin/finance/credit-packs', { packs }),
  saveMembershipTiers: (tiers) => adminApi.put('/admin/finance/membership-tiers', { tiers }),
  getCreditRechargeRecords: (params) => adminApi.get('/admin/finance/credit-records', { params }),
  getMembershipRechargeRecords: (params) => adminApi.get('/admin/finance/membership-records', { params }),
  getShopRechargeRecords: (params) => adminApi.get('/admin/finance/shop-records', { params }),
  getShopPacks: () => adminApi.get('/admin/finance/shop-packs'),
  saveShopPacks: (packs) => adminApi.put('/admin/finance/shop-packs', { packs }),
  getStoragePacks: () => adminApi.get('/admin/finance/storage-packs'),
  saveStoragePacks: (packs) => adminApi.put('/admin/finance/storage-packs', { packs }),
  getStorageRechargeRecords: (params) => adminApi.get('/admin/finance/storage-records', { params }),
  getPurchaseIntentMeta: () => adminApi.get('/admin/purchase-intents/meta'),
  listPurchaseIntents: (params) => adminApi.get('/admin/purchase-intents', { params }),
  getPurchaseIntent: (id) => adminApi.get(`/admin/purchase-intents/${encodeURIComponent(id)}`),
  startPurchaseIntentProgress: (id) => adminApi.post(`/admin/purchase-intents/${encodeURIComponent(id)}/start-progress`, {}),
  failPurchaseIntent: (id, data) => adminApi.post(`/admin/purchase-intents/${encodeURIComponent(id)}/fail`, data),
  setPurchaseIntentFlags: (data) => adminApi.post('/admin/purchase-intents/flags', data),
  clearPurchaseIntentFlags: (data) => adminApi.post('/admin/purchase-intents/flags/clear', data),
  getModulePricing: () => adminApi.get('/admin/modules/pricing'),
  updateModulePricing: (data) => adminApi.put('/admin/modules/pricing', data),
  getCommercialActivities: () => adminApi.get('/admin/activities'),
  getCommercialActivityStats: () => adminApi.get('/admin/activities/stats'),
  getCommercialActivityDashboard: () => adminApi.get('/admin/activities/dashboard'),
  createCommercialActivity: (data) => adminApi.post('/admin/activities', data),
  updateCommercialActivity: (id, data) => adminApi.put(`/admin/activities/${id}`, data),
  setCommercialActivityEnabled: (id, enabled) => adminApi.patch(`/admin/activities/${id}/enabled`, { enabled }),
  deleteCommercialActivity: (id) => adminApi.delete(`/admin/activities/${id}`),
  getCommercialActivityRecords: (params) => adminApi.get('/admin/activities/records', { params }),
  getOfficialWorkflowTemplates: () => adminApi.get('/admin/official-data/workflow-templates'),
  getOfficialWorkflowTemplate: (id) => adminApi.get(`/admin/official-data/workflow-templates/${encodeURIComponent(id)}`),
  createOfficialWorkflowTemplate: (data) => adminApi.post('/admin/official-data/workflow-templates', data),
  updateOfficialWorkflowTemplate: (id, data) => adminApi.put(`/admin/official-data/workflow-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialWorkflowTemplate: (id) => adminApi.delete(`/admin/official-data/workflow-templates/${encodeURIComponent(id)}`),
  getOfficialImageTemplates: () => adminApi.get('/admin/official-data/image-templates'),
  createOfficialImageTemplate: (data) => adminApi.post('/admin/official-data/image-templates', data),
  updateOfficialImageTemplate: (id, data) => adminApi.put(`/admin/official-data/image-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialImageTemplate: (id) => adminApi.delete(`/admin/official-data/image-templates/${encodeURIComponent(id)}`),
  listOfficialSheinProductTemplates: (params = {}) => adminApi.get('/admin/official-data/shein-product-templates', { params }),
  getOfficialSheinProductTemplate: (id) => adminApi.get(`/admin/official-data/shein-product-templates/${encodeURIComponent(id)}`),
  createOfficialSheinProductTemplate: (data) => adminApi.post('/admin/official-data/shein-product-templates', data),
  updateOfficialSheinProductTemplate: (id, data) => adminApi.put(`/admin/official-data/shein-product-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialSheinProductTemplate: (id) => adminApi.delete(`/admin/official-data/shein-product-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialSheinProductTemplates: (ids = []) => adminApi.post('/admin/official-data/shein-product-templates/batch-delete', { ids }),
  batchCreateOfficialSheinProductTemplates: (data) => adminApi.post(
    '/admin/official-data/shein-product-templates/batch-create',
    data,
    { timeout: 120000 },
  ),
  copyOfficialSheinProductTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-product-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialSheinProductTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-product-templates/${encodeURIComponent(id)}/move`, data),
  /** 官方产品模板商品图：只落 IPE，不转 SHEIN */
  storeOfficialSheinProductTemplateImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/shein-product-templates/store-image',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  getOfficialSheinCatalogCategories: (params = {}) => adminApi.get('/admin/official-data/shein-catalog/categories', {
    params: { shopType: params.shopType || undefined },
    timeout: 90000,
  }),
  getOfficialSheinCatalogSites: (params = {}) => adminApi.get('/admin/official-data/shein-catalog/sites', {
    params: { shopType: params.shopType || undefined },
  }),
  getOfficialSheinCatalogWarehouses: (params = {}) => adminApi.get('/admin/official-data/shein-catalog/warehouses', {
    params: { shopType: params.shopType || undefined },
  }),
  listOfficialSheinCatalogCertificateTypes: (params = {}) => adminApi.get(
    '/admin/official-data/shein-catalog/certificate-types',
    {
      params: { shopType: params.shopType || undefined },
      timeout: 90000,
    },
  ),
  /** 官方证书：读取本地完整填写规则（含 preset / fileModelUrl） */
  getOfficialSheinCatalogCertificateSchemasDetail: (shopType, data = {}) => adminApi.post(
    '/admin/official-data/shein-catalog/certificate-schemas/detail',
    data,
    {
      params: { shopType },
      timeout: 90000,
    },
  ),
  /** 官方证书：借同类型已授权店同步填写规则 */
  syncOfficialSheinCatalogCertificateSchemas: (shopType) => adminApi.post(
    '/admin/official-data/shein-catalog/certificate-schemas/sync',
    {},
    {
      params: { shopType },
      timeout: 120000,
    },
  ),
  /** 官方证书：代下载填写模板 */
  downloadOfficialSheinCatalogCertificateFileModel: async (
    shopType,
    { certificateTypeId, certificateTypeCode, fileName } = {},
  ) => {
    const type = String(shopType || '').trim();
    if (!type) throw new Error('请先选择店铺类型');
    const params = new URLSearchParams();
    params.set('shopType', type);
    if (certificateTypeId != null && String(certificateTypeId).trim() !== '') {
      params.set('certificateTypeId', String(certificateTypeId).trim());
    }
    if (certificateTypeCode) {
      params.set('certificateTypeCode', String(certificateTypeCode).trim());
    }
    if (!params.has('certificateTypeId') && !params.has('certificateTypeCode')) {
      throw new Error('请先选择证书类型');
    }
    const headers = buildAdminAuthHeaders();
    if (!headers.Authorization) {
      throw new Error('请先登录后再下载');
    }
    const base = process.env.REACT_APP_API_URL || '/api';
    const url = `${base}/admin/official-data/shein-catalog/certificate-schemas/file-model?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', headers, cache: 'no-store' });
    if (!response.ok) {
      let msg = '下载填写模板失败';
      try {
        const errBody = await response.json();
        if (errBody?.message) msg = String(errBody.message);
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || '证书填写模板.pdf';
    a.click();
    URL.revokeObjectURL(objectUrl);
  },
  getOfficialSheinCatalogAttributeTemplate: (data = {}) => adminApi.post(
    '/admin/official-data/shein-catalog/attribute-template',
    data,
    { timeout: 90000 },
  ),
  getOfficialSheinCatalogPublishFillInStandard: (data = {}, params = {}) => adminApi.post(
    '/admin/official-data/shein-catalog/publish-fill-in-standard',
    data,
    {
      params: { shopId: params.shopId || params.referenceShopId || undefined },
      timeout: 60000,
    },
  ),
  /**
   * 超管：清空类目/属性缓存并可异步重拉。
   * body.confirm 必须为 RESET_SHEIN_CATEGORY_CATALOG
   */
  resetOfficialSheinCategoryCatalog: (data = {}) => adminApi.post(
    '/admin/official-data/shein-catalog/reset-category-catalog',
    {
      confirm: data.confirm || 'RESET_SHEIN_CATEGORY_CATALOG',
      resync: data.resync !== false,
      shopType: data.shopType || undefined,
    },
    { timeout: 120000 },
  ),
  listOfficialSheinAttributeTemplates: (params = {}) => adminApi.get('/admin/official-data/shein-attribute-templates', { params }),
  getOfficialSheinAttributeTemplate: (id) => adminApi.get(`/admin/official-data/shein-attribute-templates/${encodeURIComponent(id)}`),
  createOfficialSheinAttributeTemplate: (data) => adminApi.post('/admin/official-data/shein-attribute-templates', data),
  updateOfficialSheinAttributeTemplate: (id, data) => adminApi.put(`/admin/official-data/shein-attribute-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialSheinAttributeTemplate: (id) => adminApi.delete(`/admin/official-data/shein-attribute-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialSheinAttributeTemplates: (ids = []) => adminApi.post('/admin/official-data/shein-attribute-templates/batch-delete', { ids }),
  batchCreateOfficialSheinAttributeTemplates: (data) => adminApi.post(
    '/admin/official-data/shein-attribute-templates/batch-create',
    data,
    { timeout: 120000 },
  ),
  copyOfficialSheinAttributeTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-attribute-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialSheinAttributeTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-attribute-templates/${encodeURIComponent(id)}/move`, data),
  listOfficialSheinSizeChartTemplates: (params = {}) => adminApi.get('/admin/official-data/shein-material-templates/size-charts', { params }),
  getOfficialSheinSizeChartTemplate: (id) => adminApi.get(`/admin/official-data/shein-material-templates/size-charts/${encodeURIComponent(id)}`),
  createOfficialSheinSizeChartTemplate: (data) => adminApi.post('/admin/official-data/shein-material-templates/size-charts', data),
  updateOfficialSheinSizeChartTemplate: (id, data) => adminApi.put(`/admin/official-data/shein-material-templates/size-charts/${encodeURIComponent(id)}`, data),
  deleteOfficialSheinSizeChartTemplate: (id) => adminApi.delete(`/admin/official-data/shein-material-templates/size-charts/${encodeURIComponent(id)}`),
  batchDeleteOfficialSheinSizeChartTemplates: (ids = []) => adminApi.post('/admin/official-data/shein-material-templates/size-charts/batch-delete', { ids }),
  batchCreateOfficialSheinSizeChartTemplates: (data) => adminApi.post(
    '/admin/official-data/shein-material-templates/size-charts/batch-create',
    data,
    { timeout: 120000 },
  ),
  copyOfficialSheinSizeChartTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-material-templates/size-charts/${encodeURIComponent(id)}/copy`, data),
  moveOfficialSheinSizeChartTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-material-templates/size-charts/${encodeURIComponent(id)}/move`, data),
  listOfficialSheinPhotoTemplates: (params = {}) => adminApi.get('/admin/official-data/shein-material-templates/photos', { params }),
  getOfficialSheinPhotoTemplate: (id) => adminApi.get(`/admin/official-data/shein-material-templates/photos/${encodeURIComponent(id)}`),
  createOfficialSheinPhotoTemplate: (data) => adminApi.post('/admin/official-data/shein-material-templates/photos', data),
  updateOfficialSheinPhotoTemplate: (id, data) => adminApi.put(`/admin/official-data/shein-material-templates/photos/${encodeURIComponent(id)}`, data),
  deleteOfficialSheinPhotoTemplate: (id) => adminApi.delete(`/admin/official-data/shein-material-templates/photos/${encodeURIComponent(id)}`),
  batchDeleteOfficialSheinPhotoTemplates: (ids = []) => adminApi.post('/admin/official-data/shein-material-templates/photos/batch-delete', { ids }),
  copyOfficialSheinPhotoTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-material-templates/photos/${encodeURIComponent(id)}/copy`, data),
  moveOfficialSheinPhotoTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-material-templates/photos/${encodeURIComponent(id)}/move`, data),
  storeOfficialSheinPhotoTemplateOriginal: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/shein-material-templates/photos/store-original',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  listOfficialSheinCertificateTemplates: (params = {}) => adminApi.get('/admin/official-data/shein-material-templates/certificates', { params }),
  getOfficialSheinCertificateTemplate: (id) => adminApi.get(`/admin/official-data/shein-material-templates/certificates/${encodeURIComponent(id)}`),
  createOfficialSheinCertificateTemplate: (data) => adminApi.post('/admin/official-data/shein-material-templates/certificates', data),
  updateOfficialSheinCertificateTemplate: (id, data) => adminApi.put(`/admin/official-data/shein-material-templates/certificates/${encodeURIComponent(id)}`, data),
  deleteOfficialSheinCertificateTemplate: (id) => adminApi.delete(`/admin/official-data/shein-material-templates/certificates/${encodeURIComponent(id)}`),
  batchDeleteOfficialSheinCertificateTemplates: (ids = []) => adminApi.post('/admin/official-data/shein-material-templates/certificates/batch-delete', { ids }),
  copyOfficialSheinCertificateTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-material-templates/certificates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialSheinCertificateTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/shein-material-templates/certificates/${encodeURIComponent(id)}/move`, data),
  storeOfficialSheinCertificateTemplateFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/shein-material-templates/certificates/store-file',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  listOfficialTemuProductTemplates: (params = {}) => adminApi.get('/admin/official-data/temu-product-templates', { params }),
  getOfficialTemuProductTemplate: (id) => adminApi.get(`/admin/official-data/temu-product-templates/${encodeURIComponent(id)}`),
  createOfficialTemuProductTemplate: (data) => adminApi.post('/admin/official-data/temu-product-templates', data),
  updateOfficialTemuProductTemplate: (id, data) => adminApi.put(`/admin/official-data/temu-product-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialTemuProductTemplate: (id) => adminApi.delete(`/admin/official-data/temu-product-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialTemuProductTemplates: (ids = []) => adminApi.post('/admin/official-data/temu-product-templates/batch-delete', { ids }),
  batchCreateOfficialTemuProductTemplates: (data) => adminApi.post(
    '/admin/official-data/temu-product-templates/batch-create',
    data,
    { timeout: 120000 },
  ),
  copyOfficialTemuProductTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-product-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTemuProductTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-product-templates/${encodeURIComponent(id)}/move`, data),
  storeOfficialTemuProductTemplateImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/temu-product-templates/store-image',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  getOfficialTemuCatalogCategories: (params = {}) => adminApi.get('/admin/official-data/temu-catalog/categories', {
    params: { shopType: params.shopType || undefined },
    timeout: 90000,
  }),
  // 官方模板新建弹窗：类目缓存只存一级，多级下钻走该接口按需拉子类目
  getOfficialTemuCatalogCategoryChildren: (params = {}) => adminApi.get('/admin/official-data/temu-catalog/category-children', {
    params: {
      shopType: params.shopType || undefined,
      parentCatId: params.parentCatId,
    },
    timeout: 60000,
  }),
  getOfficialTemuCatalogSites: (params = {}) => adminApi.get('/admin/official-data/temu-catalog/sites', {
    params: { shopType: params.shopType || undefined },
  }),
  getOfficialTemuCatalogWarehouses: (params = {}) => adminApi.get('/admin/official-data/temu-catalog/warehouses', {
    params: { shopType: params.shopType || undefined },
  }),
  listOfficialTemuCatalogCertificateTypes: (params = {}) => adminApi.get(
    '/admin/official-data/temu-catalog/certificate-types',
    {
      params: { shopType: params.shopType || undefined },
      timeout: 90000,
    },
  ),
  syncOfficialTemuCatalogCertificateTypes: (shopType) => adminApi.post(
    '/admin/official-data/temu-catalog/certificate-types/sync',
    {},
    {
      params: { shopType: shopType || undefined },
      timeout: 180000,
    },
  ),
  getOfficialTemuCatalogAttributeTemplate: (data = {}) => adminApi.post(
    '/admin/official-data/temu-catalog/attribute-template',
    data,
    { timeout: 90000 },
  ),
  getOfficialTemuCatalogAccessories: (params = {}) => adminApi.get(
    '/admin/official-data/temu-catalog/accessories',
    {
      params: {
        shopType: params.shopType || undefined,
        referenceShopId: params.referenceShopId || undefined,
        fuzzyValue: params.fuzzyValue || undefined,
        page: params.page || 1,
        pageSize: params.pageSize || 100,
      },
      timeout: 90000,
    },
  ),
  getOfficialTemuCatalogSizeChartClass: (data = {}) => adminApi.post(
    '/admin/official-data/temu-catalog/sizecharts/class',
    data,
    { timeout: 60000 },
  ),
  getOfficialTemuCatalogSizeChartMeta: (data = {}) => adminApi.post(
    '/admin/official-data/temu-catalog/sizecharts/meta',
    data,
    { timeout: 60000 },
  ),
  getOfficialTemuCatalogSizeChartSettings: (data = {}) => adminApi.post(
    '/admin/official-data/temu-catalog/sizecharts/settings',
    data,
    { timeout: 60000 },
  ),
  getOfficialTemuCatalogSizeChartFoot: (data = {}) => adminApi.post(
    '/admin/official-data/temu-catalog/sizecharts/foot',
    data,
    { timeout: 60000 },
  ),
  getOfficialTemuCatalogPublishFillInStandard: (data = {}, params = {}) => adminApi.post(
    '/admin/official-data/temu-catalog/publish-fill-in-standard',
    data,
    {
      params: { shopId: params.shopId || params.referenceShopId || undefined },
      timeout: 60000,
    },
  ),
  listOfficialTemuAttributeTemplates: (params = {}) => adminApi.get('/admin/official-data/temu-attribute-templates', { params }),
  getOfficialTemuAttributeTemplate: (id) => adminApi.get(`/admin/official-data/temu-attribute-templates/${encodeURIComponent(id)}`),
  createOfficialTemuAttributeTemplate: (data) => adminApi.post('/admin/official-data/temu-attribute-templates', data),
  updateOfficialTemuAttributeTemplate: (id, data) => adminApi.put(`/admin/official-data/temu-attribute-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialTemuAttributeTemplate: (id) => adminApi.delete(`/admin/official-data/temu-attribute-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialTemuAttributeTemplates: (ids = []) => adminApi.post('/admin/official-data/temu-attribute-templates/batch-delete', { ids }),
  batchCreateOfficialTemuAttributeTemplates: (data) => adminApi.post(
    '/admin/official-data/temu-attribute-templates/batch-create',
    data,
    { timeout: 120000 },
  ),
  copyOfficialTemuAttributeTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-attribute-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTemuAttributeTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-attribute-templates/${encodeURIComponent(id)}/move`, data),
  listOfficialTemuSizeChartTemplates: (params = {}) => adminApi.get('/admin/official-data/temu-material-templates/size-charts', { params }),
  getOfficialTemuSizeChartTemplate: (id) => adminApi.get(`/admin/official-data/temu-material-templates/size-charts/${encodeURIComponent(id)}`),
  createOfficialTemuSizeChartTemplate: (data) => adminApi.post('/admin/official-data/temu-material-templates/size-charts', data),
  updateOfficialTemuSizeChartTemplate: (id, data) => adminApi.put(`/admin/official-data/temu-material-templates/size-charts/${encodeURIComponent(id)}`, data),
  deleteOfficialTemuSizeChartTemplate: (id) => adminApi.delete(`/admin/official-data/temu-material-templates/size-charts/${encodeURIComponent(id)}`),
  batchDeleteOfficialTemuSizeChartTemplates: (ids = []) => adminApi.post('/admin/official-data/temu-material-templates/size-charts/batch-delete', { ids }),
  batchCreateOfficialTemuSizeChartTemplates: (data) => adminApi.post(
    '/admin/official-data/temu-material-templates/size-charts/batch-create',
    data,
    { timeout: 120000 },
  ),
  copyOfficialTemuSizeChartTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/size-charts/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTemuSizeChartTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/size-charts/${encodeURIComponent(id)}/move`, data),
  listOfficialTemuModelTemplates: (params = {}) => adminApi.get('/admin/official-data/temu-material-templates/models', { params }),
  getOfficialTemuModelTemplate: (id) => adminApi.get(`/admin/official-data/temu-material-templates/models/${encodeURIComponent(id)}`),
  createOfficialTemuModelTemplate: (data) => adminApi.post('/admin/official-data/temu-material-templates/models', data),
  updateOfficialTemuModelTemplate: (id, data) => adminApi.put(`/admin/official-data/temu-material-templates/models/${encodeURIComponent(id)}`, data),
  deleteOfficialTemuModelTemplate: (id) => adminApi.delete(`/admin/official-data/temu-material-templates/models/${encodeURIComponent(id)}`),
  batchDeleteOfficialTemuModelTemplates: (ids = []) => adminApi.post('/admin/official-data/temu-material-templates/models/batch-delete', { ids }),
  copyOfficialTemuModelTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/models/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTemuModelTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/models/${encodeURIComponent(id)}/move`, data),
  storeOfficialTemuModelPortrait: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/temu-material-templates/models/store-portrait',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  batchCreateOfficialTemuModelTemplates: (data = {}) => adminApi.post(
    '/admin/official-data/temu-material-templates/models/batch-create',
    data,
    { timeout: 180000 },
  ),
  listOfficialTemuPhotoTemplates: (params = {}) => adminApi.get('/admin/official-data/temu-material-templates/photos', { params }),
  getOfficialTemuPhotoTemplate: (id) => adminApi.get(`/admin/official-data/temu-material-templates/photos/${encodeURIComponent(id)}`),
  createOfficialTemuPhotoTemplate: (data) => adminApi.post('/admin/official-data/temu-material-templates/photos', data),
  updateOfficialTemuPhotoTemplate: (id, data) => adminApi.put(`/admin/official-data/temu-material-templates/photos/${encodeURIComponent(id)}`, data),
  deleteOfficialTemuPhotoTemplate: (id) => adminApi.delete(`/admin/official-data/temu-material-templates/photos/${encodeURIComponent(id)}`),
  batchDeleteOfficialTemuPhotoTemplates: (ids = []) => adminApi.post('/admin/official-data/temu-material-templates/photos/batch-delete', { ids }),
  copyOfficialTemuPhotoTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/photos/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTemuPhotoTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/photos/${encodeURIComponent(id)}/move`, data),
  batchCreateOfficialTemuPhotoTemplates: (data = {}) => adminApi.post(
    '/admin/official-data/temu-material-templates/photos/batch-create',
    data,
    { timeout: 180000 },
  ),
  storeOfficialTemuPhotoTemplateOriginal: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/temu-material-templates/photos/store-original',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  listOfficialTemuCertificateTemplates: (params = {}) => adminApi.get('/admin/official-data/temu-material-templates/certificates', { params }),
  getOfficialTemuCertificateTemplate: (id) => adminApi.get(`/admin/official-data/temu-material-templates/certificates/${encodeURIComponent(id)}`),
  createOfficialTemuCertificateTemplate: (data) => adminApi.post('/admin/official-data/temu-material-templates/certificates', data),
  updateOfficialTemuCertificateTemplate: (id, data) => adminApi.put(`/admin/official-data/temu-material-templates/certificates/${encodeURIComponent(id)}`, data),
  deleteOfficialTemuCertificateTemplate: (id) => adminApi.delete(`/admin/official-data/temu-material-templates/certificates/${encodeURIComponent(id)}`),
  batchDeleteOfficialTemuCertificateTemplates: (ids = []) => adminApi.post('/admin/official-data/temu-material-templates/certificates/batch-delete', { ids }),
  copyOfficialTemuCertificateTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/certificates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTemuCertificateTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/temu-material-templates/certificates/${encodeURIComponent(id)}/move`, data),
  batchCreateOfficialTemuCertificateTemplates: (data = {}) => adminApi.post(
    '/admin/official-data/temu-material-templates/certificates/batch-create',
    data,
    { timeout: 180000 },
  ),
  seedOfficialTemuCertificateSamples: () => adminApi.post(
    '/admin/official-data/temu-material-templates/certificates/seed-samples',
    {},
    { timeout: 120000 },
  ),
  storeOfficialTemuCertificateTemplateFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/temu-material-templates/certificates/store-file',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  /* ---------- 官方 TikTok 模板（控制台） ---------- */
  listOfficialTiktokProductTemplates: (params = {}) => adminApi.get('/admin/official-data/tiktok-product-templates', { params }),
  getOfficialTiktokProductTemplate: (id) => adminApi.get(`/admin/official-data/tiktok-product-templates/${encodeURIComponent(id)}`),
  createOfficialTiktokProductTemplate: (data) => adminApi.post('/admin/official-data/tiktok-product-templates', data),
  updateOfficialTiktokProductTemplate: (id, data) => adminApi.put(`/admin/official-data/tiktok-product-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialTiktokProductTemplate: (id) => adminApi.delete(`/admin/official-data/tiktok-product-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialTiktokProductTemplates: (ids = []) => adminApi.post('/admin/official-data/tiktok-product-templates/batch-delete', { ids }),
  copyOfficialTiktokProductTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/tiktok-product-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTiktokProductTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/tiktok-product-templates/${encodeURIComponent(id)}/move`, data),
  /** 官方 TikTok 产品模板商品图：只落 IPE，不转 TikTok（复制到店铺刊登时再转） */
  storeOfficialTiktokProductTemplateImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post(
      '/admin/official-data/tiktok-product-templates/store-image',
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  listOfficialTiktokAttributeTemplates: (params = {}) => adminApi.get('/admin/official-data/tiktok-attribute-templates', { params }),
  getOfficialTiktokAttributeTemplate: (id) => adminApi.get(`/admin/official-data/tiktok-attribute-templates/${encodeURIComponent(id)}`),
  createOfficialTiktokAttributeTemplate: (data) => adminApi.post('/admin/official-data/tiktok-attribute-templates', data),
  updateOfficialTiktokAttributeTemplate: (id, data) => adminApi.put(`/admin/official-data/tiktok-attribute-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialTiktokAttributeTemplate: (id) => adminApi.delete(`/admin/official-data/tiktok-attribute-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialTiktokAttributeTemplates: (ids = []) => adminApi.post('/admin/official-data/tiktok-attribute-templates/batch-delete', { ids }),
  copyOfficialTiktokAttributeTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/tiktok-attribute-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTiktokAttributeTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/tiktok-attribute-templates/${encodeURIComponent(id)}/move`, data),
  listOfficialTiktokSizeChartTemplates: (params = {}) => adminApi.get('/admin/official-data/tiktok-size-chart-templates', { params }),
  getOfficialTiktokSizeChartTemplate: (id) => adminApi.get(`/admin/official-data/tiktok-size-chart-templates/${encodeURIComponent(id)}`),
  createOfficialTiktokSizeChartTemplate: (data) => adminApi.post('/admin/official-data/tiktok-size-chart-templates', data),
  updateOfficialTiktokSizeChartTemplate: (id, data) => adminApi.put(`/admin/official-data/tiktok-size-chart-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialTiktokSizeChartTemplate: (id) => adminApi.delete(`/admin/official-data/tiktok-size-chart-templates/${encodeURIComponent(id)}`),
  batchDeleteOfficialTiktokSizeChartTemplates: (ids = []) => adminApi.post('/admin/official-data/tiktok-size-chart-templates/batch-delete', { ids }),
  copyOfficialTiktokSizeChartTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/tiktok-size-chart-templates/${encodeURIComponent(id)}/copy`, data),
  moveOfficialTiktokSizeChartTemplate: (id, data = {}) => adminApi.post(`/admin/official-data/tiktok-size-chart-templates/${encodeURIComponent(id)}/move`, data),
  getOfficialTiktokCatalogCategories: (params = {}) => adminApi.get('/admin/official-data/tiktok-catalog/categories', {
    params: { shopType: params.shopType || undefined },
    timeout: 90000,
  }),
  getOfficialTiktokCatalogCategoryChildren: (params = {}) => adminApi.get('/admin/official-data/tiktok-catalog/category-children', {
    params: {
      shopType: params.shopType || undefined,
      parentCatId: params.parentCatId,
    },
    timeout: 90000,
  }),
  getOfficialTiktokCatalogSites: (params = {}) => adminApi.get('/admin/official-data/tiktok-catalog/sites', {
    params: { shopType: params.shopType || undefined },
  }),
  getOfficialTiktokCatalogWarehouses: (params = {}) => adminApi.get('/admin/official-data/tiktok-catalog/warehouses', {
    params: { shopType: params.shopType || undefined },
  }),
  getOfficialTiktokCatalogAttributeTemplate: (data = {}) => adminApi.post(
    '/admin/official-data/tiktok-catalog/attribute-template',
    data,
    { timeout: 90000 },
  ),
  getOfficialTiktokCatalogPublishFillInStandard: (data = {}) => adminApi.post(
    '/admin/official-data/tiktok-catalog/publish-fill-in-standard',
    data,
    { timeout: 90000 },
  ),
  getOfficialTitleTemplates: () => adminApi.get('/admin/official-data/title-templates'),
  createOfficialTitleTemplate: (data) => adminApi.post('/admin/official-data/title-templates', data),
  updateOfficialTitleTemplate: (id, data) => adminApi.put(`/admin/official-data/title-templates/${encodeURIComponent(id)}`, data),
  deleteOfficialTitleTemplate: (id) => adminApi.delete(`/admin/official-data/title-templates/${encodeURIComponent(id)}`),
  listDesignColors: () => adminApi.get('/admin/official-data/design-colors'),
  createDesignColor: (data) => adminApi.post('/admin/official-data/design-colors', data),
  updateDesignColor: (id, data) => adminApi.put(`/admin/official-data/design-colors/${encodeURIComponent(id)}`, data),
  deleteDesignColor: (id) => adminApi.delete(`/admin/official-data/design-colors/${encodeURIComponent(id)}`),
  batchDeleteDesignColors: (ids = []) => adminApi.post('/admin/official-data/design-colors/batch-delete', { ids }),
  rebuildDesignColors: () => adminApi.post('/admin/official-data/design-colors/rebuild'),
  listOfficialText2ImgPromptCases: (params = {}) => adminApi.get('/admin/ai-text2img-prompt-cases', {
    params: {
      keyword: params.keyword,
      includeDisabled: params.includeDisabled !== false,
      page: params.page,
      pageSize: params.pageSize ?? params.size,
    },
  }),
  createOfficialText2ImgPromptCase: (data) => adminApi.post('/admin/ai-text2img-prompt-cases', data),
  updateOfficialText2ImgPromptCase: (id, data) => adminApi.put(`/admin/ai-text2img-prompt-cases/${encodeURIComponent(id)}`, data),
  deleteOfficialText2ImgPromptCase: (id) => adminApi.delete(`/admin/ai-text2img-prompt-cases/${encodeURIComponent(id)}`),
  listOfficialImg2ImgPromptCases: (params = {}) => adminApi.get('/admin/ai-img2img-prompt-cases', {
    params: {
      keyword: params.keyword,
      includeDisabled: params.includeDisabled !== false,
      page: params.page,
      pageSize: params.pageSize ?? params.size,
    },
  }),
  createOfficialImg2ImgPromptCase: (data) => adminApi.post('/admin/ai-img2img-prompt-cases', data),
  updateOfficialImg2ImgPromptCase: (id, data) => adminApi.put(`/admin/ai-img2img-prompt-cases/${encodeURIComponent(id)}`, data),
  deleteOfficialImg2ImgPromptCase: (id) => adminApi.delete(`/admin/ai-img2img-prompt-cases/${encodeURIComponent(id)}`),
  uploadOfficialImg2ImgPromptCaseCover: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.put(
      `/admin/ai-img2img-prompt-cases/${encodeURIComponent(id)}/cover`,
      formData,
      { timeout: UPLOAD_CONFIG.timeoutMs },
    ));
  },
  clearOfficialImg2ImgPromptCaseCover: (id) => adminApi.delete(
    `/admin/ai-img2img-prompt-cases/${encodeURIComponent(id)}/cover`,
  ),
  getOfficialTableTemplateRules: () => adminApi.get('/admin/official-data/table-template-rules'),
  createOfficialTableTemplateRule: (data) => adminApi.post('/admin/official-data/table-template-rules', data),
  updateOfficialTableTemplateRule: (id, data) => adminApi.put(`/admin/official-data/table-template-rules/${encodeURIComponent(id)}`, data),
  deleteOfficialTableTemplateRule: (id) => adminApi.delete(`/admin/official-data/table-template-rules/${encodeURIComponent(id)}`),
  getOfficialMaterials: (params) => adminApi.get('/admin/official-data/materials', { params }),
  getOfficialMaterialBatches: () => adminApi.get('/admin/official-data/materials/batches'),
  deleteOfficialMaterial: (id) => adminApi.delete(`/admin/official-data/materials/${encodeURIComponent(id)}`),
  batchDeleteOfficialMaterials: (ids) => adminApi.post('/admin/official-data/materials/batch-delete', { ids }),
  publishOfficialMaterials: (ids) => adminApi.post('/admin/official-data/materials/publish', { ids }),
  ingestOfficialMaterials: (data) => adminApi.post('/admin/official-data/materials/ingest', data),
  uploadOfficialMaterial: (file, { category, name, batchId } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (name) formData.append('name', name);
    if (batchId) formData.append('batchId', batchId);
    return runWithRetry(() => adminApi.post('/admin/official-data/materials/upload', formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  batchUploadOfficialMaterials: (files, { category, batchId } = {}) => {
    const formData = new FormData();
    (files || []).forEach((file) => {
      if (file) formData.append('files', file);
    });
    formData.append('category', category);
    if (batchId) formData.append('batchId', batchId);
    return runWithRetry(() => adminApi.post('/admin/official-data/materials/batch-upload', formData, {
      timeout: UPLOAD_CONFIG.batchTimeoutMs,
    }));
  },
  getFeaturedMaterials: (params) => adminApi.get('/admin/official-materials/featured', { params }),
  getFeaturedMaterial: (id) => adminApi.get(`/admin/official-materials/featured/${encodeURIComponent(id)}`),
  createFeaturedMaterial: (data) => adminApi.post('/admin/official-materials/featured', data),
  updateFeaturedMaterial: (id, data) => adminApi.put(`/admin/official-materials/featured/${encodeURIComponent(id)}`, data),
  deleteFeaturedMaterial: (id) => adminApi.delete(`/admin/official-materials/featured/${encodeURIComponent(id)}`),
  batchDeleteFeaturedMaterials: (ids) => adminApi.post('/admin/official-materials/featured/batch-delete', { ids }),
  publishFeaturedMaterials: (ids) => adminApi.post('/admin/official-materials/featured/publish', { ids }),
  uploadFeaturedMaterial: (file, { title, category, contentType, premium, score, publishStatus, batchId } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (category) formData.append('category', category);
    if (contentType) formData.append('contentType', contentType);
    if (premium != null) formData.append('premium', String(premium));
    if (score != null) formData.append('score', String(score));
    if (publishStatus) formData.append('publishStatus', publishStatus);
    if (batchId) formData.append('batchId', batchId);
    return runWithRetry(() => adminApi.post('/admin/official-materials/featured/upload', formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  batchUploadFeaturedMaterials: (files, { category, contentType, batchId } = {}) => {
    const formData = new FormData();
    (files || []).forEach((file) => {
      if (file) formData.append('files', file);
    });
    if (category) formData.append('category', category);
    if (contentType) formData.append('contentType', contentType);
    if (batchId) formData.append('batchId', batchId);
    return runWithRetry(() => adminApi.post('/admin/official-materials/featured/batch-upload', formData, {
      timeout: UPLOAD_CONFIG.batchTimeoutMs,
    }));
  },
  getFeaturedCategories: (params) => adminApi.get('/admin/official-materials/featured/categories', { params }),
  createFeaturedCategory: (data) => adminApi.post('/admin/official-materials/featured/categories', data),
  updateFeaturedCategory: (id, data) => adminApi.put(`/admin/official-materials/featured/categories/${encodeURIComponent(id)}`, data),
  deleteFeaturedCategory: (id) => adminApi.delete(`/admin/official-materials/featured/categories/${encodeURIComponent(id)}`),
  getFeaturedContentTypes: () => adminApi.get('/admin/official-materials/featured/content-types'),
  getSystemModels: (params) => adminApi.get('/admin/system-models', { params }),
  /** 与用户端 /system/models 一致：仅已上架素材，供 Admin 试衣预览槽位 */
  getPublishedSystemModelPreview: (params) => adminApi.get('/admin/system-models/published-preview', { params }),
  getSystemModel: (id) => adminApi.get(`/admin/system-models/${encodeURIComponent(id)}`),
  updateSystemModel: (id, data) => adminApi.put(`/admin/system-models/${encodeURIComponent(id)}`, data),
  replaceSystemModelImage: (id, file, { thumb } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (thumb) formData.append('thumb', thumb);
    return runWithRetry(() => adminApi.put(`/admin/system-models/${encodeURIComponent(id)}/image`, formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  uploadSystemModel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.thumb) formData.append('thumb', options.thumb);
    if (options.id) formData.append('id', options.id);
    if (options.name) formData.append('name', options.name);
    if (options.categoryId) formData.append('categoryId', options.categoryId);
    if (options.status) formData.append('status', options.status);
    if (options.modelType) formData.append('modelType', options.modelType);
    if (options.garmentType) formData.append('garmentType', options.garmentType);
    if (options.ethnicity) formData.append('ethnicity', options.ethnicity);
    if (options.frameScope) formData.append('frameScope', options.frameScope);
    if (options.poseIndex != null) formData.append('poseIndex', String(options.poseIndex));
    if (options.poseLabel) formData.append('poseLabel', options.poseLabel);
    if (options.sortOrder != null) formData.append('sortOrder', String(options.sortOrder));
    if (options.legacyPresetId) formData.append('legacyPresetId', options.legacyPresetId);
    (options.featureCodes || []).forEach((code) => {
      if (code) formData.append('featureCodes', code);
    });
    return runWithRetry(() => adminApi.post('/admin/system-models/upload', formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  getSystemModelCategories: (params) => adminApi.get('/admin/system-models/categories', { params }),
  getSystemModelCategory: (id) => adminApi.get(`/admin/system-models/categories/${encodeURIComponent(id)}`),
  createSystemModelCategory: (data) => adminApi.post('/admin/system-models/categories', data),
  updateSystemModelCategory: (id, data) => adminApi.put(`/admin/system-models/categories/${encodeURIComponent(id)}`, data),
  deleteSystemModelCategory: (id) => adminApi.delete(`/admin/system-models/categories/${encodeURIComponent(id)}`),
  reorderSystemModelCategories: (data) => adminApi.post('/admin/system-models/categories/reorder', data),
  moveSystemModelCategory: (id, data) => adminApi.post(
    `/admin/system-models/categories/${encodeURIComponent(id)}/move`,
    data,
  ),
  reorderSystemModels: (data) => adminApi.post('/admin/system-models/reorder', data),
  importSystemModelStaticPresets: (data) => adminApi.post('/admin/system-models/import-static-presets', data),
  /** 节点 11.2：扫描 public/assets/model-try-on-presets/**（仅超管） */
  scanSystemModelStaticPresets: (params) => adminApi.get('/admin/system-models/static-presets/scan', { params }),
  publishSystemModels: (ids) => adminApi.post('/admin/system-models/publish', { ids }),
  unpublishSystemModels: (ids) => adminApi.post('/admin/system-models/unpublish', { ids }),
  archiveSystemModels: (ids) => adminApi.post('/admin/system-models/archive', { ids }),
  deleteSystemModel: (id) => adminApi.delete(`/admin/system-models/${encodeURIComponent(id)}`),
  batchDeleteSystemModels: (ids) => adminApi.post('/admin/system-models/batch-delete', { ids }),
  batchMoveSystemModelsCategory: (data) => adminApi.post('/admin/system-models/batch-move-category', data),
  batchUpdateSystemModelsFeatureCodes: (data) => adminApi.post('/admin/system-models/batch-update-feature-codes', data),
  validateSystemModelBatch: (data) => adminApi.post('/admin/system-models/batch-validate', data),
  /** 工作流引用预检（POST 请求体，大批量时由调用方分片） */
  querySystemModelWorkflowReferences: (ids) => adminApi.post('/admin/system-models/workflow-references', {
    ids: Array.isArray(ids) ? ids : [],
  }),
  getSystemModelWorkflowReferences: (ids) => adminApi.post('/admin/system-models/workflow-references', {
    ids: Array.isArray(ids) ? ids : [],
  }),
  createFeaturedContentType: (data) => adminApi.post('/admin/official-materials/featured/content-types', data),
  updateFeaturedContentType: (id, data) => adminApi.put(`/admin/official-materials/featured/content-types/${encodeURIComponent(id)}`, data),
  deleteFeaturedContentType: (id) => adminApi.delete(`/admin/official-materials/featured/content-types/${encodeURIComponent(id)}`),
  submitMaterialAiTask: (data) => adminApi.post('/admin/material-ai/tasks', data),
  getMaterialAiTasks: (params) => adminApi.get('/admin/material-ai/tasks', { params }),
  getMaterialAiTask: (id) => adminApi.get(`/admin/material-ai/tasks/${encodeURIComponent(id)}`),
  cancelMaterialAiTask: (id) => adminApi.post(`/admin/material-ai/tasks/${encodeURIComponent(id)}/cancel`),
  batchDeleteMaterialAiTasks: (ids) => adminApi.post('/admin/material-ai/tasks/batch-delete', { ids }),
  getTutorialUsers: (params) => adminApi.get('/admin/tutorial-users', { params }),
  getTutorialEditorApplications: (params) =>
    adminApi.get('/admin/tutorial-users/editor-applications', { params }),
  approveTutorialEditorApplication: (id) =>
    adminApi.post(`/admin/tutorial-users/${id}/approve-editor`),
  rejectTutorialEditorApplication: (id) =>
    adminApi.post(`/admin/tutorial-users/${id}/reject-editor`),
  createTutorialUser: (payload) => adminApi.post('/admin/tutorial-users', payload),
  updateTutorialUser: (id, payload) => adminApi.put(`/admin/tutorial-users/${id}`, payload),
  deleteTutorialUser: (id) => adminApi.delete(`/admin/tutorial-users/${id}`),

  getPlatformTutorials: (params) => adminApi.get('/admin/platform-tutorials', { params }),
  createPlatformTutorial: (payload) => adminApi.post('/admin/platform-tutorials', payload),
  updatePlatformTutorial: (id, payload) => adminApi.put(`/admin/platform-tutorials/${encodeURIComponent(id)}`, payload),
  deletePlatformTutorial: (id) => adminApi.delete(`/admin/platform-tutorials/${encodeURIComponent(id)}`),
  batchDeletePlatformTutorials: (ids) => adminApi.post('/admin/platform-tutorials/batch-delete', { ids }),

  getSystemAnnouncements: (params) => adminApi.get('/admin/system-announcements', { params }),
  getSystemAnnouncement: (id) => adminApi.get(`/admin/system-announcements/${encodeURIComponent(id)}`),
  createSystemAnnouncement: (payload) => adminApi.post('/admin/system-announcements', payload),
  updateSystemAnnouncement: (id, payload) => adminApi.put(`/admin/system-announcements/${encodeURIComponent(id)}`, payload),
  setSystemAnnouncementEnabled: (id, enabled) => adminApi.patch(`/admin/system-announcements/${encodeURIComponent(id)}/enabled`, { enabled }),
  deleteSystemAnnouncement: (id) => adminApi.delete(`/admin/system-announcements/${encodeURIComponent(id)}`),
  uploadSystemAnnouncementImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post('/admin/system-announcements/upload-image', formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  uploadPlatformTutorialFile: (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    if (type) formData.append('type', type);
    // 视频可达 500MB，超时需长于普通图片上传
    const timeoutMs = String(type || '').toUpperCase() === 'VIDEO'
      ? Math.max(UPLOAD_CONFIG.timeoutMs, 600_000)
      : UPLOAD_CONFIG.timeoutMs;
    return runWithRetry(() => adminApi.post('/admin/platform-tutorials/upload', formData, {
      timeout: timeoutMs,
    }));
  },
  uploadPlatformTutorialCover: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return runWithRetry(() => adminApi.post('/admin/platform-tutorials/upload-cover', formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
};

/** 浏览器插件采集上报 Admin 素材推荐（须 Admin Token，路径见 client/docs/plugin-capture-api.md） */
export const pluginCaptureAdminAPI = {
  publishOfficialMaterials: (ids) => adminApi.post('/admin/official-data/materials/publish', { ids }),
  ingestOfficialMaterials: (data) => adminApi.post('/plugin/capture/official-materials/ingest', data),
  uploadOfficialMaterial: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.category) formData.append('category', options.category);
    if (options.name) formData.append('name', options.name);
    if (options.batchId) formData.append('batchId', options.batchId);
    if (options.pageUrl) formData.append('pageUrl', options.pageUrl);
    if (options.pageTitle) formData.append('pageTitle', options.pageTitle);
    if (options.siteName) formData.append('siteName', options.siteName);
    return runWithRetry(() => adminApi.post('/plugin/capture/official-materials/upload', formData, {
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  batchUploadOfficialMaterials: (files, options = {}) => {
    const formData = new FormData();
    (files || []).forEach((file) => {
      if (file) formData.append('files', file);
    });
    if (options.category) formData.append('category', options.category);
    if (options.batchId) formData.append('batchId', options.batchId);
    if (options.pageUrl) formData.append('pageUrl', options.pageUrl);
    if (options.pageTitle) formData.append('pageTitle', options.pageTitle);
    if (options.siteName) formData.append('siteName', options.siteName);
    return runWithRetry(() => adminApi.post('/plugin/capture/official-materials/batch-upload', formData, {
      timeout: UPLOAD_CONFIG.batchTimeoutMs,
    }));
  },
};

export default adminAPI;
